import moment from 'moment';
import { pickField, hasValue } from './maintenanceComplianceUtils';

export const PUSPAKOM_RESULT_OPTIONS = ['Pass', 'Fail', 'Conditional'];

export const defaultComplianceForm = () => ({
  roadTaxExpiry: '',
  puspakomLastDate: '',
  puspakomNextDue: '',
  puspakomResult: '',
  puspakomCertNumber: '',
  puspakomCentre: '',
  insuranceProvider: '',
  insurancePolicyNumber: '',
  insuranceExpiry: '',
});

export const mapComplianceToForm = (data) => {
  const puspakom = data?.puspakom || data?.puspaKom || {};
  const insurance = data?.insurance || {};
  return {
    roadTaxExpiry: pickField(data, 'roadTaxExpiry', 'road_tax_expiry') || '',
    puspakomLastDate: pickField(puspakom, 'lastDate', 'last_date') || '',
    puspakomNextDue: pickField(puspakom, 'nextDue', 'next_due') || '',
    puspakomResult: pickField(puspakom, 'result') || '',
    puspakomCertNumber: pickField(puspakom, 'certNumber', 'cert_number') || '',
    puspakomCentre: pickField(puspakom, 'centre', 'center') || '',
    insuranceProvider: pickField(insurance, 'provider') || '',
    insurancePolicyNumber: pickField(insurance, 'policyNumber', 'policy_number') || '',
    insuranceExpiry: pickField(insurance, 'expiry') || '',
  };
};

export const buildSectionCompliancePayload = (section, form) => {
  if (section === 'roadTax') {
    return { roadTaxExpiry: form.roadTaxExpiry || null };
  }
  if (section === 'puspakom') {
    return {
      puspakom: {
        lastDate: form.puspakomLastDate || null,
        nextDue: form.puspakomNextDue || null,
        result: form.puspakomResult || null,
        certNumber: form.puspakomCertNumber || null,
        centre: form.puspakomCentre || null,
      },
    };
  }
  if (section === 'insurance') {
    return {
      insurance: {
        provider: form.insuranceProvider || null,
        policyNumber: form.insurancePolicyNumber || null,
        expiry: form.insuranceExpiry || null,
      },
    };
  }
  return {};
};

const getSectionClearFields = (section) => {
  if (section === 'roadTax') return { roadTaxExpiry: '' };
  if (section === 'puspakom') {
    return {
      puspakomLastDate: '',
      puspakomNextDue: '',
      puspakomResult: '',
      puspakomCertNumber: '',
      puspakomCentre: '',
    };
  }
  if (section === 'insurance') {
    return {
      insuranceProvider: '',
      insurancePolicyNumber: '',
      insuranceExpiry: '',
    };
  }
  return {};
};

export const sectionRenewLabels = {
  roadTax: 'Renew',
  puspakom: 'Inspection',
  insurance: 'Renew',
};

/** True when the section has any saved field values. */
export const hasSectionData = (section, form) => {
  const clearFields = getSectionClearFields(section);
  return Object.keys(clearFields).some((key) => hasValue(form[key]));
};

/** True when the section has the primary date required before renew can archive it. */
export const canRenewSection = (section, form) => {
  if (section === 'roadTax') return hasValue(form.roadTaxExpiry);
  if (section === 'puspakom') return hasValue(form.puspakomNextDue);
  if (section === 'insurance') return hasValue(form.insuranceExpiry);
  return false;
};

/** Previous section snapshot used for renew validation (server archives from persisted compliance). */
export const getSectionPreviousData = (section, compliance) => {
  const form = mapComplianceToForm(compliance || {});
  if (!canRenewSection(section, form)) return null;

  if (section === 'roadTax') {
    return { roadTaxExpiry: form.roadTaxExpiry };
  }

  if (section === 'puspakom') {
    return {
      lastDate: form.puspakomLastDate || null,
      nextDue: form.puspakomNextDue || null,
      result: form.puspakomResult || null,
      certNumber: form.puspakomCertNumber || null,
      centre: form.puspakomCentre || null,
    };
  }

  if (section === 'insurance') {
    return {
      provider: form.insuranceProvider || null,
      policyNumber: form.insurancePolicyNumber || null,
      expiry: form.insuranceExpiry || null,
    };
  }

  return null;
};

export const buildSectionRenewPayload = (section, form) => ({
  section,
  ...buildSectionCompliancePayload(section, form),
});

export const buildBlankSectionForm = (section) => ({
  ...defaultComplianceForm(),
  ...getSectionClearFields(section),
});

export const minRenewDateValue = () => moment().add(1, 'day').format('YYYY-MM-DD');

const isSameCalendarDay = (left, right) => {
  if (!hasValue(left) || !hasValue(right)) return false;
  const leftDate = moment(left).startOf('day');
  const rightDate = moment(right).startOf('day');
  return leftDate.isValid() && rightDate.isValid() && leftDate.isSame(rightDate);
};

const isOnOrBeforeToday = (value) => {
  if (!hasValue(value)) return true;
  const parsed = moment(value).startOf('day');
  if (!parsed.isValid()) return true;
  return parsed.isSameOrBefore(moment().startOf('day'));
};

const getRenewedSectionSaveError = (section, form, previousData) => {
  if (section === 'roadTax') {
    const newDate = form.roadTaxExpiry;
    const previousDate = previousData?.roadTaxExpiry;
    if (!hasValue(newDate)) return 'Enter an expiry date.';
    if (isSameCalendarDay(newDate, previousDate)) {
      return 'The new expiry date must be different from the previous one.';
    }
    if (isOnOrBeforeToday(newDate)) {
      return 'The new expiry date must be after today.';
    }
    return null;
  }

  if (section === 'puspakom') {
    const newDate = form.puspakomNextDue;
    const previousDate = previousData?.nextDue;
    if (!hasValue(newDate)) return 'Enter the next due date.';
    if (isSameCalendarDay(newDate, previousDate)) {
      return 'The new due date must be different from the previous one.';
    }
    if (isOnOrBeforeToday(newDate)) {
      return 'The new due date must be after today.';
    }
    if (hasValue(form.puspakomLastDate) && hasValue(newDate)) {
      const lastDate = moment(form.puspakomLastDate).startOf('day');
      const nextDue = moment(newDate).startOf('day');
      if (lastDate.isValid() && nextDue.isValid() && nextDue.isBefore(lastDate)) {
        return 'Next due date must be on or after the last inspection date.';
      }
    }
    return null;
  }

  if (section === 'insurance') {
    const newDate = form.insuranceExpiry;
    const previousDate = previousData?.expiry;
    if (!hasValue(form.insuranceProvider)) return 'Enter the insurance provider.';
    if (!hasValue(newDate)) return 'Enter an expiry date.';
    if (isSameCalendarDay(newDate, previousDate)) {
      return 'The new expiry date must be different from the previous one.';
    }
    if (isOnOrBeforeToday(newDate)) {
      return 'The new expiry date must be after today.';
    }
    return null;
  }

  return null;
};

export const getSectionSaveError = (section, form, modalMode, previousData) => {
  if (modalMode === 'add' && previousData) {
    return getRenewedSectionSaveError(section, form, previousData);
  }

  if (section === 'puspakom') {
    if (!hasValue(form.puspakomNextDue)) return 'Enter the next due date.';
    if (hasValue(form.puspakomLastDate) && hasValue(form.puspakomNextDue)) {
      const lastDate = moment(form.puspakomLastDate).startOf('day');
      const nextDue = moment(form.puspakomNextDue).startOf('day');
      if (lastDate.isValid() && nextDue.isValid() && nextDue.isBefore(lastDate)) {
        return 'Next due date must be on or after the last inspection date.';
      }
    }
  }

  if (section === 'roadTax' && !hasValue(form.roadTaxExpiry)) {
    return 'Enter an expiry date.';
  }

  if (section === 'insurance' && !hasValue(form.insuranceExpiry)) {
    return 'Enter an expiry date.';
  }

  return null;
};

export const complianceModalTitles = {
  edit: {
    roadTax: 'Edit road tax',
    puspakom: 'Edit PUSPAKOM inspection',
    insurance: 'Edit insurance',
  },
  add: {
    roadTax: 'Add new road tax',
    puspakom: 'Add new PUSPAKOM inspection',
    insurance: 'Add new insurance',
  },
};

export const validateServiceScheduleForm = (scheduleForm) => {
  if (!hasValue(scheduleForm.lastServiceDate) || !hasValue(scheduleForm.lastServiceOdometer)) {
    return 'Last service date and mileage are required.';
  }

  const odometer = Number(scheduleForm.lastServiceOdometer);
  if (!Number.isFinite(odometer) || odometer < 0) {
    return 'Last service mileage must be a non-negative number.';
  }

  const intervalKm = Number(scheduleForm.serviceIntervalKm);
  const intervalMonths = Number(scheduleForm.serviceIntervalMonths);
  if (!hasValue(scheduleForm.serviceIntervalKm) || !Number.isFinite(intervalKm) || intervalKm <= 0) {
    return 'Service interval (km) must be a positive number.';
  }
  if (!hasValue(scheduleForm.serviceIntervalMonths) || !Number.isFinite(intervalMonths) || intervalMonths <= 0) {
    return 'Service interval (months) must be a positive number.';
  }

  return null;
};
